import React, { useEffect, useState } from "react";
import { countries } from "countries-list";
import MetaData from "../../components/layout/MetaData";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { FaCreditCard, FaPaypal, FaShieldAlt } from "react-icons/fa";
import {
  savePaymentMethod,
  saveShippingInfo,
} from "../../redux/features/cartSlice";
import { caluclateOrderCost } from "../../helpers/caluclateOrderCost";
import { useEstimateShippingCostMutation } from "../../redux/api/orderApi";
import {
  buildCartShippingDebugItems,
  buildShippingEstimatePayload,
  extractEstimatedShippingFee,
  extractShippingQuoteOptions,
  formatShippingQuoteOptionLabel,
  formatShippingQuoteSelectedLabel,
  logCountryCodeDebug,
  logShippingEstimateDebug,
} from "../../helpers/shippingEstimate";
import "./CheckoutPage.css";

/**
 * 根据已有收货信息生成可编辑的账单地址初始值。
 * @param {Object} shippingInfo 收货信息
 * @param {Object} user 当前用户
 * @returns {Object} 账单地址表单值
 */
const createBillingAddress = (shippingInfo = {}, user = {}) => ({
  country: shippingInfo.country || "United States",
  firstName: shippingInfo.firstName || user.name || "",
  lastName: shippingInfo.lastName || "",
  companyName: shippingInfo.companyName || "",
  mailStop: shippingInfo.mailStop || "",
  addressLine1: shippingInfo.addressLine1 || shippingInfo.address || "",
  addressLine2: shippingInfo.addressLine2 || "",
  city: shippingInfo.city || "",
  stateProvince: shippingInfo.stateProvince || "",
  postalCode: shippingInfo.postalCode || shippingInfo.zipCode || "",
});

/**
 * DigiKey 风格支付方式页：选择支付渠道并进入确认订单页。
 */
const PaymentMethod = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { cartItems, shippingInfo, paymentMethod } = useSelector(
    (state) => state.cart
  );
  const { user } = useSelector((state) => state.auth);
  const [method, setMethod] = useState(paymentMethod || "Paypal");
  const [billingAddress, setBillingAddress] = useState(() =>
    createBillingAddress(shippingInfo, user)
  );
  const [estimatedShippingPrice, setEstimatedShippingPrice] = useState(null);
  const [shippingPriceSource, setShippingPriceSource] = useState("fallback");
  const [shippingEstimateError, setShippingEstimateError] = useState("");
  const [shippingQuoteOptions, setShippingQuoteOptions] = useState([]);
  const [selectedShippingQuoteId, setSelectedShippingQuoteId] = useState("");
  const [isShippingQuoteSelectExpanded, setIsShippingQuoteSelectExpanded] = useState(false);
  const [isEstimatingShipping, setIsEstimatingShipping] = useState(false);
  const [estimateShippingCost] = useEstimateShippingCostMutation();
  const countriesList = Object.values(countries);
  const {
    itemsPrice,
    shippingPrice: fallbackShippingPrice,
    taxPrice,
  } = caluclateOrderCost(cartItems);
  const selectedShippingQuote = shippingQuoteOptions.find(
    (option) => option.id === selectedShippingQuoteId
  );
  const shippingPrice =
    selectedShippingQuote?.fee ??
    (estimatedShippingPrice === null ? fallbackShippingPrice : estimatedShippingPrice);
  const isShippingEstimateFailed = shippingPriceSource === "failed";
  const totalPrice = (
    Number(itemsPrice) +
    Number(shippingPrice) +
    Number(taxPrice)
  ).toFixed(2);
  const itemCount = cartItems.reduce(
    (total, item) => total + Number(item?.quantity || 0),
    0
  );

  useEffect(() => {
    let isActive = true;

    /**
     * 根据购物车与当前国家调用 4PX 运费预估。
     */
    const estimatePaymentShipping = async () => {
      logShippingEstimateDebug("PaymentMethod", "进入运费预估流程", {
        cartItemCount: cartItems?.length || 0,
        rawCountry: billingAddress.country,
        fallbackShippingPrice,
        itemsPrice,
      });

      if (!cartItems?.length) {
        logShippingEstimateDebug("PaymentMethod", "回退到固定运费：购物车为空", {
          fallbackShippingPrice,
        });
        setEstimatedShippingPrice(null);
        setShippingPriceSource("fallback");
        setShippingEstimateError("");
        setShippingQuoteOptions([]);
        setSelectedShippingQuoteId("");
        setIsEstimatingShipping(false);
        return;
      }

      const {
        payload: estimatePayload,
        countryCode,
        totalWeightInGrams,
        debugItems,
      } = buildShippingEstimatePayload(cartItems, billingAddress.country);
      logCountryCodeDebug("PaymentMethod", billingAddress.country, countryCode);
      logShippingEstimateDebug("PaymentMethod", "4PX 请求前置参数检查", {
        rawCountry: billingAddress.country,
        countryCode,
        totalWeightInGrams,
        cartItems: debugItems || buildCartShippingDebugItems(cartItems),
      });
      if (!countryCode || !estimatePayload) {
        logShippingEstimateDebug("PaymentMethod", "回退到固定运费：收货国家无法识别为国家码", {
          rawCountry: billingAddress.country,
          fallbackShippingPrice,
        });
        setEstimatedShippingPrice(null);
        setShippingPriceSource("failed");
        setShippingEstimateError("Shipping estimate failed / Contact us");
        setShippingQuoteOptions([]);
        setSelectedShippingQuoteId("");
        setIsEstimatingShipping(false);
        return;
      }

      setIsEstimatingShipping(true);
      logShippingEstimateDebug("PaymentMethod", "准备请求 4PX 运费预估", {
        payload: estimatePayload,
        rawCountry: billingAddress.country,
        fallbackShippingPrice,
        itemsPrice,
        cartItems: debugItems || buildCartShippingDebugItems(cartItems),
      });

      try {
        const estimateResponse = await estimateShippingCost(estimatePayload).unwrap();
        if (!isActive) {
          return;
        }

        logShippingEstimateDebug("PaymentMethod", "收到后端 4PX 运费预估响应", {
          response: estimateResponse,
        });

        const quoteOptions = extractShippingQuoteOptions(
          estimateResponse,
          "PaymentMethod"
        );
        const savedShippingQuoteCode =
          shippingInfo?.selectedShippingOption?.logisticsProductCode;
        const nextSelectedQuote =
          quoteOptions.find(
            (option) => option.logisticsProductCode === savedShippingQuoteCode
          ) || quoteOptions[0];
        const estimatedFee = nextSelectedQuote?.fee ?? extractEstimatedShippingFee(
          estimateResponse,
          "PaymentMethod"
        );
        if (Number.isFinite(estimatedFee)) {
          logShippingEstimateDebug("PaymentMethod", "使用 4PX 运费预估金额", {
            estimatedFee,
            selectedShippingQuote: nextSelectedQuote || null,
            quoteOptions,
            fallbackShippingPrice,
          });
          setEstimatedShippingPrice(estimatedFee);
          setShippingQuoteOptions(quoteOptions);
          setSelectedShippingQuoteId(nextSelectedQuote?.id || "");
          setShippingPriceSource("4px");
          setShippingEstimateError("");
          return;
        }

        logShippingEstimateDebug("PaymentMethod", "回退到固定运费：4PX 响应中未解析到可用费用字段", {
          fallbackShippingPrice,
          response: estimateResponse,
        });
      } catch (estimateError) {
        if (!isActive) {
          return;
        }
        console.error("[PaymentMethod] 4PX 运费预估失败，停止显示固定兜底运费:", {
          error: estimateError,
          fallbackShippingPrice,
        });
        setShippingEstimateError("Shipping estimate failed / Contact us");
      } finally {
        if (isActive) {
          setIsEstimatingShipping(false);
        }
      }

      if (isActive) {
        logShippingEstimateDebug("PaymentMethod", "4PX 运费预估失败，停止显示固定兜底运费", {
          fallbackShippingPrice,
        });
        setEstimatedShippingPrice(null);
        setShippingPriceSource("failed");
        setShippingQuoteOptions([]);
        setSelectedShippingQuoteId("");
        setShippingEstimateError((currentError) => (
          currentError || "Shipping estimate failed / Contact us"
        ));
      }
    };

    estimatePaymentShipping();

    return () => {
      isActive = false;
    };
  }, [
    cartItems,
    billingAddress.country,
    estimateShippingCost,
    fallbackShippingPrice,
    itemsPrice,
    shippingInfo?.selectedShippingOption?.logisticsProductCode,
  ]);

  /**
   * 更新客户选择的 4PX 物流渠道。
   * @param {React.ChangeEvent<HTMLSelectElement>} event 渠道下拉变更事件
   */
  const handleShippingQuoteChange = (event) => {
    const nextQuoteId = event.target.value;
    const nextQuote = shippingQuoteOptions.find((option) => option.id === nextQuoteId);
    setSelectedShippingQuoteId(nextQuoteId);
    setIsShippingQuoteSelectExpanded(false);
    if (nextQuote) {
      setEstimatedShippingPrice(nextQuote.fee);
      logShippingEstimateDebug("PaymentMethod", "客户选择 4PX 物流渠道", {
        selectedShippingQuote: nextQuote,
      });
    }
  };

  /**
   * 标记物流渠道下拉进入展开状态，展开时显示完整渠道信息。
   */
  const handleShippingQuoteSelectExpand = () => {
    setIsShippingQuoteSelectExpanded(true);
  };

  /**
   * 标记物流渠道下拉离开展开状态，收起预览只显示价格。
   */
  const handleShippingQuoteSelectCollapse = () => {
    setIsShippingQuoteSelectExpanded(false);
  };

  /**
   * 更新指定账单地址字段。
   * @param {React.ChangeEvent<HTMLInputElement|HTMLSelectElement>} event 字段变更事件
   */
  const handleBillingFieldChange = (event) => {
    const { name, value } = event.target;
    setBillingAddress((currentAddress) => ({
      ...currentAddress,
      [name]: value,
    }));
  };

  /**
   * 保存支付方式并进入订单确认页。
   * @param {React.FormEvent<HTMLFormElement>} event 表单提交事件
   */
  const submitHandler = (event) => {
    event.preventDefault();
    if (isShippingEstimateFailed) {
      logShippingEstimateDebug("PaymentMethod", "阻止继续结算：运费预估失败", {
        shippingEstimateError,
      });
      return;
    }

    dispatch(
      saveShippingInfo({
        ...shippingInfo,
        ...billingAddress,
        address: billingAddress.addressLine1,
        zipCode: billingAddress.postalCode,
        selectedShippingOption: selectedShippingQuote || null,
      })
    );
    dispatch(savePaymentMethod(method));
    navigate("/confirm_order");
  };

  return (
    <>
      <MetaData title="Payment Method" />
      <main className="checkout-reference-page">
        <div className="checkout-reference-container">
          <nav className="checkout-breadcrumb" aria-label="Breadcrumb">
            <Link to="/cart">
              <i className="fas fa-shopping-cart" aria-hidden="true"></i>
              Shopping Cart
            </Link>
            <span>/</span>
            <strong>Payment</strong>
          </nav>

          <div className="checkout-review-bars" aria-label="Checkout progress">
            <div>
              <span>Shipping Address</span>
              <Link to="/shipping">Edit</Link>
            </div>
            <div>
              <span>Delivery Details</span>
              <Link to="/shipping">Edit</Link>
            </div>
          </div>

          <form className="checkout-reference-layout" onSubmit={submitHandler}>
            <section className="checkout-main-column">
              <h1>Payment Method</h1>
              <div className="checkout-panel payment-method-panel">
                <div className="payment-choice-list" role="radiogroup" aria-label="Payment method">
                  <label className={`payment-choice ${method === "Paypal" ? "is-selected" : ""}`}>
                    <input
                      type="radio"
                      name="payment_mode"
                      value="Paypal"
                      checked={method === "Paypal"}
                      onChange={(event) => setMethod(event.target.value)}
                    />
                    <FaPaypal aria-hidden="true" />
                    <span>Pay with PayPal</span>
                  </label>
                  <label className={`payment-choice ${method === "WireTransfer" ? "is-selected" : ""}`}>
                    <input
                      type="radio"
                      name="payment_mode"
                      value="WireTransfer"
                      checked={method === "WireTransfer"}
                      onChange={(event) => setMethod(event.target.value)}
                    />
                    <FaCreditCard aria-hidden="true" />
                    <span>Wire Transfer</span>
                  </label>
                </div>

                <label className="billing-same-address">
                  <input type="checkbox" checked readOnly />
                  <span>My billing address is the same as my shipping address</span>
                </label>

                <div className="checkout-section-heading">
                  <h2>Billing Address</h2>
                </div>
                <div className="checkout-address-form">
                  <label className="checkout-address-field checkout-address-country">
                    <span>Country/Region *</span>
                    <select name="country" value={billingAddress.country} onChange={handleBillingFieldChange} required>
                      {countriesList.map((countryItem) => (
                        <option key={countryItem.name} value={countryItem.name}>{countryItem.name}</option>
                      ))}
                    </select>
                  </label>

                  <label className="checkout-address-field">
                    <span>First Name *</span>
                    <input name="firstName" value={billingAddress.firstName} onChange={handleBillingFieldChange} required />
                  </label>
                  <label className="checkout-address-field">
                    <span>Last Name *</span>
                    <input name="lastName" value={billingAddress.lastName} onChange={handleBillingFieldChange} required />
                  </label>
                  <label className="checkout-address-field checkout-address-wide">
                    <span>Company Name</span>
                    <input name="companyName" value={billingAddress.companyName} onChange={handleBillingFieldChange} />
                  </label>
                  <label className="checkout-address-field checkout-address-wide">
                    <span>Mail Stop</span>
                    <input name="mailStop" value={billingAddress.mailStop} onChange={handleBillingFieldChange} />
                  </label>
                  <label className="checkout-address-field checkout-address-wide">
                    <span>Address Line 1 *</span>
                    <input name="addressLine1" value={billingAddress.addressLine1} onChange={handleBillingFieldChange} required />
                  </label>
                  <label className="checkout-address-field checkout-address-wide">
                    <span>Address Line 2</span>
                    <input name="addressLine2" value={billingAddress.addressLine2} onChange={handleBillingFieldChange} />
                  </label>
                  <label className="checkout-address-field">
                    <span>City *</span>
                    <input name="city" value={billingAddress.city} onChange={handleBillingFieldChange} required />
                  </label>
                  <label className="checkout-address-field">
                    <span>State/Province *</span>
                    <input name="stateProvince" value={billingAddress.stateProvince} onChange={handleBillingFieldChange} required />
                  </label>
                  <label className="checkout-address-field checkout-address-postal">
                    <span>Postal Code *</span>
                    <input name="postalCode" value={billingAddress.postalCode} onChange={handleBillingFieldChange} required />
                  </label>
                </div>

                <button type="submit" className="checkout-primary-button" disabled={isShippingEstimateFailed}>
                  Continue
                </button>
              </div>
            </section>

            <aside className="checkout-sidebar">
              <section className="checkout-summary-card">
                <h2>Summary</h2>
                <dl>
                  <div>
                    <dt>Items in cart:</dt>
                    <dd>{itemCount}</dd>
                  </div>
                  <div>
                    <dt>Subtotal:</dt>
                    <dd>${itemsPrice}</dd>
                  </div>
                  <div>
                    <dt>Shipping:</dt>
                    <dd className={isShippingEstimateFailed ? "checkout-summary-error" : ""}>
                      {isEstimatingShipping
                        ? "Estimating..."
                        : isShippingEstimateFailed
                          ? shippingEstimateError || "Shipping estimate failed / Contact us"
                          : shippingQuoteOptions.length > 0
                            ? (
                              <select
                                className="checkout-shipping-select"
                                value={selectedShippingQuoteId}
                                onChange={handleShippingQuoteChange}
                                onMouseDown={handleShippingQuoteSelectExpand}
                                onKeyDown={handleShippingQuoteSelectExpand}
                                onBlur={handleShippingQuoteSelectCollapse}
                              >
                                {shippingQuoteOptions.map((option) => (
                                  <option key={option.id} value={option.id}>
                                    {isShippingQuoteSelectExpanded || option.id !== selectedShippingQuoteId
                                      ? formatShippingQuoteOptionLabel(option)
                                      : formatShippingQuoteSelectedLabel(option)}
                                  </option>
                                ))}
                              </select>
                            )
                            : `$${Number(shippingPrice).toFixed(2)}`}
                    </dd>
                  </div>
                  {shippingPriceSource === "4px" && (
                    <div>
                      <dt>Shipping source:</dt>
                      <dd>Estimated by 4PX</dd>
                    </div>
                  )}
                  <div className="checkout-summary-total">
                    <dt>Total:</dt>
                    <dd>{isShippingEstimateFailed ? "Pending" : `$${totalPrice}`}</dd>
                  </div>
                </dl>
              </section>
              <div className="checkout-security-card">
                <FaShieldAlt aria-hidden="true" />
                <span>Your data will be encrypted and transmitted securely.</span>
              </div>
            </aside>
          </form>
        </div>
      </main>
    </>
  );
};

export default PaymentMethod;
